import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { Alert, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomTypography } from "sipli-fleet-core/components";
import { ARROW_DOWN_ICON, LOADER_ICON } from "src/assets/svgs";
import {
  useQueryGetDeviceIncomingLastValue,
  useQueryGetDeviceMappings,
} from "src/queries/mapping.queries";
import { cn } from "../../../../../../sipli-fleet-core/utils/cn";
import {
  getMappingsByPacketCode,
  isPacketCodePresent,
  normalizeFormPrimitive,
  safeString,
} from "./mapping.helpers";
import type { MappingCardContext, MappingRecord } from "./mapping.types";
import MappingAddEdit from "./MappingAddEdit";
import MappingCardHeader from "./MappingCardHeader";
import { AllMappingsDrawer, MappingDetailsDrawer } from "./MappingDetails";
import MappingEmptyState from "./MappingEmptyState";
import MappingPresetActions from "./MappingPresets";
import MappingSingleCard from "./MappingSingleCard";
import { useEpochFormatter } from "../../../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";

interface MappingPageProps {
  unitId?: string | number;
  vehicleId?: string | number;
  modelId?: string | number;
}

const MappingPage = ({ unitId, vehicleId, modelId }: MappingPageProps) => {
  const { t, i18n } = useTranslation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [showAllCards, setShowAllCards] = useState<Record<string, boolean>>({});
  const [isAllMappingsOpen, setIsAllMappingsOpen] = useState(false);
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [cardContext, setCardContext] = useState<MappingCardContext | null>(null);
  const [detailsContext, setDetailsContext] = useState<MappingCardContext | null>(null);

  const incomingQuery = useQueryGetDeviceIncomingLastValue(unitId ? { unitId } : undefined, {
    enabled: !!unitId,
  });
  const mappingQuery = useQueryGetDeviceMappings(unitId, { enabled: !!unitId });
  const incomingValues = useMemo(
    () => incomingQuery.data?.data?.response ?? {},
    [incomingQuery.data?.data?.response]
  );
  const mappings = useMemo<MappingRecord[]>(
    () => mappingQuery.data?.data?.response?.linkedMappings ?? [],
    [mappingQuery.data?.data?.response?.linkedMappings]
  );
  const hasMappings = mappings.length > 0;
  const deviceMappingId = mappingQuery.data?.data?.response?.id;
  const rawDataCount = useMemo(
    () =>
      Object.values(incomingValues).reduce((total, group) => total + Object.keys(group).length, 0),
    [incomingValues]
  );
  const isFetching = incomingQuery.isFetching || mappingQuery.isFetching;
  const formatEpoch = useEpochFormatter();

  useEffect(() => {
    const nextOpenGroups = Object.keys(incomingValues).reduce<Record<string, boolean>>(
      (acc, key) => {
        acc[key] = openGroups[key] ?? true;
        return acc;
      },
      {}
    );
    setOpenGroups(nextOpenGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingValues]);

  const openAddMapping = (context: MappingCardContext | null = null) => {
    setCardContext(context);
    setIsAddEditOpen(true);
  };

  const closeAddMapping = () => {
    setCardContext(null);
    setIsAddEditOpen(false);
  };

  const handleRefresh = () => {
    if (isFetching) return;
    incomingQuery.refetch();
    mappingQuery.refetch();
  };

  if (!unitId) {
    return (
      <div className="rounded-[16px] bg-white p-5">
        <Alert type="warning" message={t("unit_required", { defaultValue: "Unit is required" })} />
      </div>
    );
  }

  return (
    <div className="rounded-[16px] bg-white p-5">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <CustomTypography.CustomText className="!text-[19.93px] !leading-[28px] !font-bold">
          {t("monitoring_and_configuring_data", {
            defaultValue: "Monitoring and configuring data",
          })}
        </CustomTypography.CustomText>
        <div className="flex flex-wrap items-center gap-2">
          <MappingPresetActions
            unitId={unitId}
            modelId={modelId}
            deviceMappingId={deviceMappingId}
            hasMappings={hasMappings}
          />
          <button
            type="button"
            disabled={isFetching}
            onClick={handleRefresh}
            className="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[4px] border border-gray5 bg-white disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("refresh")}
          >
            <img src={LOADER_ICON} alt="" className={cn({ "animate-spin": isFetching })} />
          </button>
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (hasMappings) setIsAllMappingsOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          if (hasMappings) setIsAllMappingsOpen(true);
        }}
        className={cn(
          "mb-4 flex min-h-[64px] w-full items-center justify-between rounded-[8px] border border-primary bg-gray17 hover:bg-primary6 px-4 text-left",
          {
            "cursor-pointer": hasMappings,
            "cursor-default": !hasMappings,
          }
        )}
      >
        <span className="flex items-center gap-[10px]">
          <span className="text-[17.72px] font-bold leading-[28px] text-primary">
            {t("mappings")}
          </span>
          <CustomTag interactive={hasMappings} className="flex min-w-[38px] justify-center bg-primary text-white">
            {mappings.length}
          </CustomTag>
        </span>
        <span className="flex items-center gap-4">
          <CustomButton
            label={t("addMapping", { defaultValue: "Add mapping" })}
            type="primary"
            onClick={(event) => {
              event.stopPropagation();
              openAddMapping(null);
            }}
            className="rounded-[4px] px-6 h-[32px] !text-[14px] !font-semibold !leading-[18px] !space-x-[0.05px] !text-gray2"
          />
          <img src={ARROW_DOWN_ICON} alt="" className="-rotate-90" />
        </span>
      </div>

      <div className="rounded-[12px] border border-gray5 bg-white p-4">
        <div className="mb-4 flex items-center gap-[10px]">
          <CustomTypography.CustomText className="!text-[17.72px] !font-bold !leading-[28px] !text-black">
            {t("raw_data", { defaultValue: "Raw data" })}
          </CustomTypography.CustomText>
          <span className="flex h-[22px] items-center justify-center rounded-[24px] bg-primary px-[12px] text-[14px] font-semibold leading-none text-white">
            {formatNumber(rawDataCount, i18n.language)}
          </span>
        </div>

        {incomingQuery.isLoading ||
        mappingQuery.isLoading ||
        incomingQuery.isFetching ||
        mappingQuery.isFetching ? (
          <div className="flex min-h-[220px] items-center justify-center">
            <Spin />
          </div>
        ) : incomingQuery.isError || mappingQuery.isError ? (
          <Alert type="error" message={t("error")} />
        ) : Object.keys(incomingValues).length === 0 ? (
          <MappingEmptyState />
        ) : (
          <div>
            {Object.entries(incomingValues).map(([groupName, groupValue]) => {
              const groupEntries = Object.keys(groupValue);
              const sortedKeys = [...groupEntries].sort((a, b) => {
                const packetCodeA = groupValue[a]?.packet_code;
                const packetCodeB = groupValue[b]?.packet_code;
                const isMatchA = isPacketCodePresent(mappings, packetCodeA);
                const isMatchB = isPacketCodePresent(mappings, packetCodeB);

                return isMatchA === isMatchB ? 0 : isMatchA ? -1 : 1;
              });
              const matchedKeys = sortedKeys.filter((key) =>
                isPacketCodePresent(mappings, groupValue[key]?.packet_code)
              );
              const hasAnyMatchedData = matchedKeys.length > 0;
              const hasAllMatchedData = matchedKeys.length === sortedKeys.length;
              const visibleKeys =
                !hasAnyMatchedData || showAllCards[groupName] ? sortedKeys : matchedKeys;

              return (
                <div key={groupName}>
                  <MappingCardHeader
                    title={safeString(groupName)}
                    count={groupEntries.length}
                    open={Boolean(openGroups[groupName])}
                    onToggle={() =>
                      setOpenGroups((prev) => ({
                        ...prev,
                        [groupName]: !prev[groupName],
                      }))
                    }
                  />
                  {openGroups[groupName] && (
                    <div className="py-5">
                      {visibleKeys.length === 0 ? (
                        <div className="p-2">{t("no_data")}</div>
                      ) : (
                        <div className="flex flex-wrap gap-4">
                          {visibleKeys.map((childKey) => {
                            const entry = groupValue[childKey];
                            const firstValue = entry?.data?.find(Boolean);
                            const packetCode = entry?.packet_code ?? "";
                            const mappingData = getMappingsByPacketCode(mappings, packetCode);
                            const context: MappingCardContext = {
                              parentKey: groupName,
                              childKey,
                              packetCode,
                              lastValue:
                                firstValue?.value && typeof firstValue.value === "object"
                                  ? 0
                                  : normalizeFormPrimitive(firstValue?.value),
                              currentValue:
                                firstValue?.value && typeof firstValue.value === "object"
                                  ? 0
                                  : normalizeFormPrimitive(firstValue?.value),
                              entry: entry ?? { packet_code: packetCode, data: [] },
                              mappingData,
                            };

                            return (
                              <MappingSingleCard
                                key={childKey}
                                title={safeString(childKey)}
                                value={safeString(firstValue?.value)}
                                updatedAt={safeString(
                                  formatEpoch(firstValue?.lastUpdate, "HH:mm:ss")
                                )}
                                receivedAt={safeString(
                                  formatEpoch(firstValue?.receivedAt, "HH:mm:ss")
                                )}
                                recentData={entry?.data ?? []}
                                mappings={mappingData}
                                vehicleId={vehicleId}
                                onSettingsClick={() => openAddMapping(context)}
                                onDetailsClick={() => setDetailsContext(context)}
                                enableAddMappingAction={true}
                              />
                            );
                          })}
                        </div>
                      )}
                      {hasAnyMatchedData && !hasAllMatchedData && (
                        <div className="mt-5 text-center flex justify-center">
                          <CustomTypography.CustomText
                            onClick={() =>
                              setShowAllCards((prev) => ({
                                ...prev,
                                [groupName]: !prev[groupName],
                              }))
                            }
                            className="flex w-fit justify-center items-center gap-2.5 cursor-pointer select-none !text-primary !font-[600] !leading-[18px] !space-x-[0.05px]"
                          >
                            {showAllCards[groupName]
                              ? t("hide_unmapped_data", { defaultValue: "Hide unmapped data" })
                              : t("show_unmapped_data", { defaultValue: "Show unmapped data" })}
                            <img
                              src={ARROW_DOWN_ICON}
                              alt=""
                              className={cn({
                                "rotate-180": showAllCards[groupName],
                              })}
                            />
                          </CustomTypography.CustomText>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AllMappingsDrawer
        open={isAllMappingsOpen}
        onClose={() => setIsAllMappingsOpen(false)}
        unitId={unitId}
        incomingValues={incomingValues}
        mappings={mappings}
        drawerWidth={393}
      />
      <MappingDetailsDrawer
        open={Boolean(detailsContext)}
        onClose={() => setDetailsContext(null)}
        unitId={unitId}
        incomingValues={incomingValues}
        mappings={detailsContext?.mappingData ?? []}
        availabilityMappings={mappings}
        parentKey={detailsContext?.parentKey}
        childKey={detailsContext?.childKey}
        drawerWidth={393}
      />
      <MappingAddEdit
        open={isAddEditOpen}
        unitId={unitId}
        incomingValues={incomingValues}
        mappings={mappings}
        context={cardContext}
        onClose={closeAddMapping}
      />
    </div>
  );
};

export default MappingPage;
